import { autoinject, bindable } from "aurelia-framework";
import { GridOptions, ICellEditorParams, CellValueChangedEvent } from "ag-grid";
import { Datasource, DatasourceFactory } from "../../services/datasource";
import { KGridEditorType } from "../../components/k-grid-editor";
import { HttpResponseMessage } from "aurelia-http-client";
import { AuthService } from "../../services/auth";
import { ComponentAttached } from "aurelia-templating";

@autoinject
export class Config {
  @bindable() advancedParams: boolean = false;
  @bindable() changed: boolean = false;
  @bindable() errorMessage?: string;
  @bindable() editable: boolean = false;

  configOptions: GridOptions = {
    enableServerSideSorting: true,
    enableServerSideFilter: true,
    enableColResize: true,
    rowModelType: "infinite",
    paginationPageSize: 100,
    onCellValueChanged: this.onCellValueChanged,
    context: {
      // remember this view model, for later use in callbacks
      viewModel: this,
      // getEditorType: used by the k-grid-editor component which is used for the value column
      getEditorType: this.getEditorType,
    },
  };

  private datasource: Datasource;

  constructor(
    datasourceFactory: DatasourceFactory,
    private authService: AuthService,
  ) {
    this.editable = authService.hasRight("admin-write");
    this.datasource = datasourceFactory.createDatasource("admin/config");
    this.configOptions.datasource =
      this.datasource.createGridDatasourceServerSideFiltering(() => {
        return { advancedParams: this.advancedParams };
      }, [{ colId: "name", sort: "asc" }]);
  }

  onGridSizeChanged() {
    this.configOptions.api!.sizeColumnsToFit();
  }

  save() {
    let updatedRows: any[] = [];
    this.configOptions.api!.getModel().forEachNode((row) => {
      if (row.data.$status == "changed") updatedRows.push(row.data);
    });
    this.datasource
      .update(updatedRows)
      .then(() => {
        this.reload();
      })
      .catch((e: HttpResponseMessage) => {
        this.errorMessage =
          "unexpected error: " +
          e.statusCode +
          " " +
          e.statusText +
          (e.response ? ": " + e.response : "");
      });
  }

  revert() {
    this.reload();
  }

  reload() {
    this.configOptions.api!.purgeInfiniteCache();
    this.changed = false;
    this.errorMessage = undefined;
  }

  /** config parameter type -> grid editor type. */
  private getEditorType(params: ICellEditorParams): KGridEditorType {
    // possible values for parameter type: string, password, stringlist, int, intlist, long, boolean, duration
    const configType = params.node.data.type;
    return configType == "int"
      ? KGridEditorType.int
      : configType == "intlist"
        ? KGridEditorType.intlist
        : configType == "long"
          ? KGridEditorType.int
          : configType == "boolean"
            ? KGridEditorType.boolean
            : configType == "duration"
              ? KGridEditorType.duration
              : configType == "password"
                ? KGridEditorType.password
                : KGridEditorType.string;
  }

  private onCellValueChanged(params: CellValueChangedEvent) {
    console.log(params);
    // mark row as changed
    params.data.$status = "changed";
    params.api.redrawRows(); // needed to redraw the 'name' column, too (to make it blue)

    // mark the form as changed
    // note: we cannot access 'this' here; 'this' is a callback function
    // but luckily we put the viewModel into our custom 'context' object
    params.context.viewModel.changed = true;
  }
}
